import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { handleApiBadges } from "@/lib/badges";
import { exportMonthlyReportPDF } from "@/lib/pdf-export";
import {
  ArrowLeft, FileText, Download, TrendingUp, UtensilsCrossed,
  Calendar, MessageSquare, Loader2, Activity,
} from "lucide-react";
import DisciplineBadge from "@/components/training/DisciplineBadge";
import { getDiscipline } from "@/lib/trainingDisciplines";

function shiftMonth(monthStr, delta) {
  const [y, m] = monthStr.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function StudentReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(currentMonth);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/reports/monthly/${id}`, { params: { month } })
      .then(r => setReport(r.data))
      .catch(err => {
        toast.error(err.response?.data?.detail || "Erreur de chargement");
        navigate("/coach/students");
      })
      .finally(() => setLoading(false));
  }, [id, month, navigate]);

  const handleExport = async () => {
    if (!report || !id) return;
    exportMonthlyReportPDF(report);
    try {
      const res = await api.post(`/reports/log-export/${id}`);
      handleApiBadges(res, toast);
    } catch { /* non bloquant */ }
    toast.success("PDF généré !");
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  if (!report) return null;

  return (
    <div data-testid="student-report-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/coach/students/${id}`)} className="mt-1">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Rapport Mensuel</h1>
            <p className="text-slate-500 mt-1">{report.student?.name} — {report.month_label}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, -1))}>←</Button>
          <input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-1.5 text-sm"
          />
          <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, 1))}>→</Button>
          <Button onClick={handleExport} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full gap-1.5">
            <Download className="w-4 h-4" /> Export PDF
          </Button>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {report.weight_stats && Object.keys(report.weight_stats).length > 0 && (
          <Card className="border-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-medium text-slate-500">Poids</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {report.weight_stats.start} → {report.weight_stats.end} kg
              </p>
              <Badge className={`mt-2 ${report.weight_stats.diff <= 0 ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                {report.weight_stats.diff > 0 ? "+" : ""}{report.weight_stats.diff} kg
              </Badge>
            </CardContent>
          </Card>
        )}

        <Card className="border-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-slate-500">Check-ins</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{report.checkin_count}</p>
            {report.averages?.adherence != null && (
              <p className="text-xs text-slate-500 mt-1">Adhérence moy. {report.averages.adherence}/10</p>
            )}
          </CardContent>
        </Card>

        {report.meal_stats && (
          <Card className="border-green-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <UtensilsCrossed className="w-4 h-4 text-green-600" />
                <span className="text-xs font-medium text-slate-500">Journal alimentaire</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{report.meal_stats.compliance_pct}%</p>
              <p className="text-xs text-slate-500 mt-1">
                {report.meal_stats.logged_days}/{report.meal_stats.days_in_month} jours
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-slate-600" />
              <span className="text-xs font-medium text-slate-500">Plans actifs</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {(report.diet_plans?.length || 0) + (report.training_plans?.length || 0)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {report.diet_plans?.length || 0} diet · {report.training_plans?.length || 0} training
            </p>
          </CardContent>
        </Card>

        {report.workout_stats?.sessions_count > 0 && (
          <Card className="border-orange-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-orange-600" />
                <span className="text-xs font-medium text-slate-500">Séances loguées</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{report.workout_stats.sessions_count}</p>
              <p className="text-xs text-slate-500 mt-1">
                {report.workout_stats.total_distance_km != null && `${report.workout_stats.total_distance_km} km`}
                {report.workout_stats.total_distance_km != null && report.workout_stats.total_volume_kg != null && " · "}
                {report.workout_stats.total_volume_kg != null && `${report.workout_stats.total_volume_kg} kg vol.`}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {report.training_plans?.length > 0 && (
        <Card className="mt-4 border-orange-100">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Programmes d&apos;entraînement</h3>
            <div className="space-y-2">
              {report.training_plans.map((p) => {
                const d = getDiscipline(p.discipline || "strength");
                const Icon = d.icon;
                return (
                  <div key={p.plan_id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${d.light}`}>
                        <Icon className={`w-4 h-4 ${d.accent}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{p.title}</p>
                        <p className="text-xs text-slate-500">{p.days_count || 0} jours</p>
                      </div>
                    </div>
                    <DisciplineBadge discipline={p.discipline || "strength"} size="sm" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Averages grid */}
      {report.averages && (
        <Card className="mt-6 border-slate-100">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Moyennes bien-être</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { key: "energy", label: "Énergie" },
                { key: "sleep", label: "Sommeil" },
                { key: "adherence", label: "Adhérence" },
                { key: "mood", label: "Humeur" },
              ].map(({ key, label }) => (
                <div key={key} className="text-center bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="text-xl font-bold text-slate-900 mt-1">
                    {report.averages[key] != null ? `${report.averages[key]}/10` : "—"}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress logs */}
      {report.progress_logs?.length > 0 && (
        <Card className="mt-4 border-slate-100">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Mesures de progression</h3>
            <div className="space-y-2">
              {report.progress_logs.map((p, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-slate-50 pb-2">
                  <span className="text-slate-500">{p.date}</span>
                  <span className="font-medium">{p.weight ? `${p.weight} kg` : "—"}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coach comments */}
      {report.coach_comments?.length > 0 && (
        <Card className="mt-4 border-purple-100">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-purple-600" /> Commentaires coach
            </h3>
            <div className="space-y-3">
              {report.coach_comments.map((c, i) => (
                <div key={i} className="bg-purple-50 rounded-xl p-3">
                  <p className="text-xs text-purple-600 font-medium">Semaine du {c.week_start}</p>
                  <p className="text-sm text-slate-700 mt-1">{c.coach_comment}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Meal details */}
      {report.meal_stats?.avg_calories != null && (
        <Card className="mt-4 border-green-100">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-900 mb-2">Nutrition moyenne</h3>
            <div className="flex gap-4 text-sm">
              <span><strong>{report.meal_stats.avg_calories}</strong> kcal/jour</span>
              <span><strong>{report.meal_stats.avg_protein}</strong> g protéines/jour</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}